import { useId, useState } from 'react';
import { Check, Copy, Share2 } from 'lucide-react';

export function CopyLinkButton({
  link,
  label = 'Copy link',
  linkLabel = 'Link to copy',
}: {
  link: string;
  label?: string;
  linkLabel?: string;
}) {
  const linkId = useId();
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setCopyFailed(false);
    } catch {
      setCopied(false);
      setCopyFailed(true);
    }
  };

  return (
    <div className="relative inline-flex">
      <button
        type="button"
        className={`btn-icon ${copied ? 'text-emerald-700' : ''}`}
        aria-label={label}
        title={copied ? 'Link copied' : label}
        onClick={copyLink}
      >
        {copied ? (
          <Check className="h-4 w-4" aria-hidden="true" />
        ) : (
          <span className="relative inline-block h-4 w-4" aria-hidden="true">
            <Share2 className="h-4 w-4" />
            <Copy className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-sm bg-white p-px text-brand-700" />
          </span>
        )}
      </button>
      <label className="sr-only" htmlFor={linkId}>
        {linkLabel}
      </label>
      <input
        id={linkId}
        className="sr-only"
        readOnly
        value={link}
        tabIndex={-1}
        onFocus={(event) => event.currentTarget.select()}
      />
      <span className="sr-only" role="status" aria-live="polite">
        {copied ? 'Link copied.' : ''}
      </span>
      {copyFailed ? (
        <div className="absolute right-0 top-full z-20 mt-2 w-72 max-w-[80vw] rounded-lg border border-amber-200 bg-white p-3 text-left shadow-card">
          <p className="text-xs text-amber-800">Copy was blocked. Select and copy the link.</p>
          <input
            className="input mt-2 text-xs"
            aria-label={linkLabel}
            readOnly
            value={link}
            onFocus={(event) => event.currentTarget.select()}
          />
        </div>
      ) : null}
    </div>
  );
}
